import {
  Checkbox,
  CheckpointsIcon,
  Box as DarkBox,
  FeedItem,
  MapPinIcon,
} from '@hivewatch/dark'

import { DragEventI, Draggable } from '@/components/ui'
import { CheckpointIProps } from '@/features/floor-plan'
import { useAppSelector } from '@/redux/hooks'
import { selectMe } from '@/redux/me/meSlice'
import { trackEvent } from '@/utils/analytics'

import { FLOOR_PLAN_CHECKPOINTS_ANALYTICS } from '../../constants/constants'
import { darkCheckPointsPanelStyles } from './DarkCheckPointsPanel.styles'

interface DarkCheckpointRowIProps {
  checkpoint: CheckpointIProps
  colorScheme: 'dark' | 'light'
  isEditMode: boolean
  isSelected: boolean
  onCheckPointClick: (checkpoint: CheckpointIProps) => void
  onDeviceItemHoverIn?: (deviceId: string) => void
  onDeviceItemHoverOut?: () => void
  onTileDragStart?: (event: DragEventI) => void
  onToggleSelect: () => void
}

// One row per checkpoint: bulk-print checkbox beside a FeedItem. Placed rows
// highlight their map pin on hover; unplaced rows are draggable onto the map
// in edit mode (legacy UnPlacedCheckpointListItem parity, same event names).
export const DarkCheckpointRow = ({
  checkpoint,
  colorScheme,
  isEditMode,
  isSelected,
  onCheckPointClick,
  onDeviceItemHoverIn,
  onDeviceItemHoverOut,
  onTileDragStart,
  onToggleSelect,
}: DarkCheckpointRowIProps) => {
  const me = useAppSelector(selectMe)
  const s = darkCheckPointsPanelStyles(colorScheme)
  const isPlaced = checkpoint.isPlaced

  const analyticsProps = {
    checkpoint_id: checkpoint.id,
    facility_id: checkpoint.facility?.id,
    floor_id: checkpoint.floor?.id,
    role: me?.roleName,
  }

  const item = (
    <FeedItem
      ariaLabel={checkpoint.name}
      colorScheme={colorScheme}
      icon={isPlaced ? <MapPinIcon size={20} /> : <CheckpointsIcon size={20} />}
      message={checkpoint.name}
      onClick={() => onCheckPointClick(checkpoint)}
      timestamp={checkpoint.displayId}
    />
  )

  return (
    <DarkBox
      data-testid='floorPlanPage_darkCheckpointRow'
      onMouseEnter={
        isPlaced ? () => onDeviceItemHoverIn?.(checkpoint.id) : undefined
      }
      onMouseLeave={isPlaced ? () => onDeviceItemHoverOut?.() : undefined}
      {...s.row}
    >
      <Checkbox
        checked={isSelected}
        colorScheme={colorScheme}
        data-testid='floorPlanPage_darkCheckpointRow_select'
        onChange={onToggleSelect}
      />
      <DarkBox {...s.rowItem}>
        {isPlaced ? (
          item
        ) : (
          <Draggable
            dragData={checkpoint}
            dragEnabled={isEditMode}
            onDragEnded={() => {
              trackEvent(
                FLOOR_PLAN_CHECKPOINTS_ANALYTICS.mapCheckpointEndMoving,
                analyticsProps
              )
            }}
            onDragStart={(e: DragEventI) => {
              trackEvent(
                FLOOR_PLAN_CHECKPOINTS_ANALYTICS.mapCheckpointMovePlacement,
                analyticsProps
              )
              onTileDragStart?.(e)
            }}
          >
            {item}
          </Draggable>
        )}
      </DarkBox>
    </DarkBox>
  )
}
